class WristStrapDrawer extends HTMLElement {
  constructor() {
    super();

    this.handleExternalOpen = this.handleExternalOpen.bind(this);
    this.selectedOptions = {};
    this.previousScrollY = null;
  }

  connectedCallback() {
    this.overlay = this.querySelector('[data-wrist-strap-overlay]');
    this.panel = this.querySelector('[data-wrist-strap-panel]');
    this.closeButtons = this.querySelectorAll('[data-wrist-strap-close]');
    this.addButton = this.querySelector('[data-wrist-strap-add]');
    this.priceDisplay = this.querySelector('[data-wrist-strap-price-display]');
    this.errorEl = this.querySelector('[data-wrist-strap-error]');
    this.variantData = this.parseVariantData();
    this.currency = this.dataset.currencySymbol || '£';
    this.discountEnabled = this.dataset.discountEnabled === 'true';
    this.discountPercent = parseFloat(this.dataset.discountPercent) || 0;
    this.discountNote = this.dataset.discountNote || '';

    this.optionInputs = Array.from(
      this.querySelectorAll('.wrist-strap-drawer__option-values input[type="radio"]'),
    );
    this.mediaGallery = this.querySelector('[data-wrist-strap-gallery]');
    this.galleryId = this.dataset.galleryId;
    this.optionLabels = Array.from(this.querySelectorAll('[data-option-label-position]'));
    this.ctaText = this.querySelector('[data-wrist-strap-cta-text]');
    this.cartContext = null;
    this.cartDrawer = document.querySelector('cart-drawer');

    this.defaultVariantId = Number(this.dataset.defaultVariantId);
    this.selectedVariant =
      this.variantData.find((variant) => variant.id === this.defaultVariantId) || this.variantData[0];

    if (this.selectedVariant) {
      this.selectedVariant.options.forEach((value, index) => {
        this.selectedOptions[index + 1] = value;
      });
    }

    this.attachEvents();
    this.updateOptionInputs();
    this.updateDisplays();
    this.updateGallery();
    this.updateCtaLabel();
  }

  disconnectedCallback() {
    document.removeEventListener('wrist-strap:open', this.handleExternalOpen);
  }

  attachEvents() {
    this.overlay?.addEventListener('click', () => this.close());
    this.closeButtons.forEach((btn) => btn.addEventListener('click', () => this.close()));
    this.addButton?.addEventListener('click', () => this.addToProduct());
    this.optionInputs.forEach((input) =>
      input.addEventListener('change', (event) => this.handleOptionChange(event)),
    );

    document.addEventListener('wrist-strap:open', this.handleExternalOpen);
  }

  parseVariantData() {
    try {
      const jsonNode = this.querySelector('[data-wrist-strap-variants]');
      if (!jsonNode) return [];
      return JSON.parse(jsonNode.textContent);
    } catch (error) {
      console.warn('Unable to parse wrist strap variant data', error);
      return [];
    }
  }

  handleExternalOpen(event) {
    const { button } = event.detail || {};
    if (button) {
      this.activeTrigger = button;
    } else {
      this.activeTrigger = document.querySelector('[data-wrist-strap-trigger]');
    }
    this.cartContext = event.detail?.cartContext || null;
    this.updateCtaLabel();
    this.closeCartDrawer();
    const requestedVariantId = Number(event.detail?.component?.variantId);
    const applied = this.applyVariantById(requestedVariantId);
    if (!applied) {
      this.syncSelectionFromWidget();
    }
    this.open();
  }

  syncSelectionFromWidget() {
    const widget = document.querySelector('[data-wrist-strap-widget]');
    if (!widget) return;
    const selectedId = Number(widget.dataset.selectedVariantId || widget.dataset.defaultVariantId || this.dataset.defaultVariantId);
    this.applyVariantById(selectedId);
  }

  applyVariantById(variantId) {
    if (!variantId || Number.isNaN(variantId)) return false;
    const variant = this.variantData.find((item) => item.id === variantId);
    if (!variant) return false;
    this.selectedVariant = variant;
    this.selectedOptions = {};
    variant.options.forEach((value, index) => {
      this.selectedOptions[index + 1] = value;
    });
    this.updateOptionInputs();
    this.updateOptionLabels();
    this.updateDisplays();
    this.updateGallery();
    return true;
  }

  open() {
    if (window.innerWidth <= 750) {
      this.previousScrollY = window.scrollY || 0;
    }
    document.body.classList.add('overflow-hidden');
    this.setAttribute('aria-hidden', 'false');
    this.classList.add('is-open');
  }

  close() {
    document.body.classList.remove('overflow-hidden');
    this.setAttribute('aria-hidden', 'true');
    this.classList.remove('is-open');
    if (this.previousScrollY !== null) {
      window.scrollTo({ top: this.previousScrollY, behavior: 'auto' });
      this.previousScrollY = null;
    }
    if (this.errorEl) this.errorEl.textContent = '';
    this.cartContext = null;
    this.updateCtaLabel();
  }

  updateSelectedVariant() {
    const match = this.variantData.find((variant) =>
      variant.options.every((value, index) => this.selectedOptions[index + 1] === value),
    );
    this.selectedVariant = match || null;
    this.updateDisplays();
    this.updateOptionInputs();
    this.updateGallery();
  }

  handleOptionChange(event) {
    const input = event.currentTarget;
    if (input.disabled) return;
    const optionPosition = this.getOptionPosition(input);
    this.selectedOptions[optionPosition] = input.value;
    this.ensureDependentOptionsAreAvailable(optionPosition);
    this.updateSelectedVariant();
  }

  ensureDependentOptionsAreAvailable(changedPosition) {
    const totalOptions = this.variantData?.[0]?.options?.length || 0;
    if (!totalOptions || changedPosition >= totalOptions) return;

    for (let optionPosition = changedPosition + 1; optionPosition <= totalOptions; optionPosition += 1) {
      const currentValue = this.selectedOptions[optionPosition];
      const hasValidCurrentValue =
        currentValue && this.isOptionValueAvailable(optionPosition, currentValue);

      if (hasValidCurrentValue) continue;

      const firstAvailableInput = this.optionInputs.find((input) => {
        const inputOptionPosition = this.getOptionPosition(input);
        if (inputOptionPosition !== optionPosition) return false;
        return this.isOptionValueAvailable(optionPosition, input.value);
      });

      if (firstAvailableInput) {
        this.selectedOptions[optionPosition] = firstAvailableInput.value;
      } else {
        delete this.selectedOptions[optionPosition];
      }
    }
  }

  getOptionPosition(input) {
    const optionAttr = input.getAttribute('data-option');
    if (optionAttr && optionAttr.includes('option')) {
      return Number(optionAttr.replace('option', ''));
    }
    const group = input.closest('[data-option-position]');
    return Number(group?.dataset.optionPosition);
  }

  updateOptionInputs() {
    if (!this.optionInputs?.length) return;
    this.optionInputs.forEach((input) => {
      const optionPosition = this.getOptionPosition(input);
      const shouldCheck = this.selectedOptions[optionPosition] === input.value;
      input.checked = shouldCheck;
      input.nextElementSibling?.classList.toggle('is-active', shouldCheck);
    });
    this.updateOptionLabels();
    this.updateOptionAvailability();
  }

  updateGallery() {
    if (!this.mediaGallery) return;

    const colorValue = this.getSelectedColor();
    const galleryKey = this.galleryId;
    const featuredMediaId = this.selectedVariant?.featured_media?.id;
    const preferredKey = galleryKey && featuredMediaId ? `${galleryKey}-${featuredMediaId}` : null;

    const slides = Array.from(this.mediaGallery.querySelectorAll('.product__media-item'));
    if (!slides.length) return;

    const getMediaAlt = (element) => {
      if (!element) return '';
      return (
        element.dataset.mediaAlt ||
        element.getAttribute('data-media-alt') ||
        element.querySelector('img')?.getAttribute('alt') ||
        ''
      ).toLowerCase();
    };

    const matchesColor = (element) => {
      if (!colorValue) return false;
      const mediaAlt = getMediaAlt(element);
      const normalizedAlt = mediaAlt.replace(/[^a-z0-9]/g, '');
      return normalizedAlt.includes(colorValue);
    };

    const colorSlides = colorValue ? slides.filter((slide) => matchesColor(slide)) : [];
    const defaultSlides = slides.filter((slide) => {
      const alt = slide.dataset.mediaAlt?.toLowerCase();
      return !alt || (!alt.includes('color_') && !alt.includes('alphabet_'));
    });
    const visibleSlides =
      colorSlides.length > 0 ? colorSlides : defaultSlides.length > 0 ? defaultSlides : slides;

    slides.forEach((slide) => {
      const shouldShow = visibleSlides.includes(slide);
      slide.classList.toggle('is-hidden', !shouldShow);
      slide.classList.toggle('product__media-item--variant', !shouldShow);
    });

    const targetSlide =
      visibleSlides.find((slide) => preferredKey && slide.dataset.mediaId === preferredKey) ||
      visibleSlides.find(
        (slide) =>
          featuredMediaId && slide.dataset.mediaId?.endsWith(`-${featuredMediaId}`),
      ) ||
      visibleSlides[0];

    if (!targetSlide) return;

    slides.forEach((slide) => slide.classList.remove('is-active'));
    targetSlide.classList.add('is-active');

    const sliderComponent = this.mediaGallery.querySelector('slider-component');
    sliderComponent?.resetPages?.();

    const sliderTrack = sliderComponent?.querySelector('[id^="Slider-"]');
    if (sliderTrack) {
      sliderTrack.scrollTo({
        left: targetSlide.offsetLeft,
        behavior: 'smooth',
      });
    }

    const visibleTargets = new Set(visibleSlides.map((slide) => slide.dataset.mediaId));
    const thumbnailItems = Array.from(this.mediaGallery.querySelectorAll('.thumbnail-list__item'));
    thumbnailItems.forEach((item) => {
      const target = item.dataset.target;
      const button = item.querySelector('button');
      let shouldShow = visibleTargets.has(target);

      if (!shouldShow && colorValue) {
        const altAttr = getMediaAlt(item);
        const normalizedAlt = altAttr.replace(/[^a-z0-9]/g, '');
        if (normalizedAlt.includes(colorValue)) {
          shouldShow = true;
        }
      }

      item.classList.toggle('is-hidden', !shouldShow);
      item.classList.toggle('thumbnail-list_item--variant', !shouldShow);
      if (shouldShow && target === targetSlide.dataset.mediaId) {
        button?.setAttribute('aria-current', 'true');
      } else {
        button?.removeAttribute('aria-current');
      }
    });
  }

  getSelectedColor() {
    const activeInput = this.optionInputs.find(
      (input) => input.checked && input.name?.toLowerCase().includes('color'),
    );
    if (activeInput?.value) {
      return this.normalizeColorValue(activeInput.value);
    }

    const option1 = this.selectedVariant?.option1;
    if (option1) {
      return this.normalizeColorValue(option1);
    }

    return null;
  }

  getDisplayColor() {
    const activeInput = this.optionInputs.find(
      (input) => input.checked && input.name?.toLowerCase().includes('color'),
    );
    if (activeInput?.value) {
      return activeInput.value;
    }

    return this.selectedVariant?.option1 || this.selectedVariant?.options?.[0] || null;
  }

  normalizeColorValue(value) {
    if (!value) return null;
    return value.toLowerCase().replace(/[^a-z0-9]/g, '');
  }

  updateOptionLabels() {
    if (!this.optionLabels?.length) return;
    this.optionLabels.forEach((label) => {
      const position = Number(label.dataset.optionLabelPosition);
      const value = this.selectedOptions[position];
      if (value) {
        label.textContent = value;
      }
    });
  }

  updateOptionAvailability() {
    if (!this.optionInputs?.length || !this.variantData?.length) return;

    this.optionInputs.forEach((input) => {
      const optionPosition = this.getOptionPosition(input);
      const isAvailable = this.isOptionValueAvailable(optionPosition, input.value);
      const label = input.nextElementSibling;

      input.disabled = !isAvailable;
      input.classList.toggle('disabled', !isAvailable);
      label?.classList.toggle('out-of-stock', !isAvailable);
    });
  }

  isOptionValueAvailable(optionPosition, optionValue) {
    return this.variantData.some((variant) => {
      if (!variant?.available) return false;
      if (variant.options?.[optionPosition - 1] !== optionValue) return false;

      for (let posNumber = 1; posNumber < optionPosition; posNumber += 1) {
        const selectedValue = this.selectedOptions[posNumber];
        if (!selectedValue) continue;
        if (variant.options?.[posNumber - 1] !== selectedValue) return false;
      }

      return true;
    });
  }

  updateDisplays() {
    if (!this.selectedVariant) {
      if (this.priceDisplay) this.priceDisplay.textContent = 'Unavailable';
      if (this.addButton) this.addButton.disabled = true;
      return;
    }

    const variantPrice = this.getEffectivePrice(this.selectedVariant.price);
    const priceHtml = this.getPriceHtml(variantPrice, this.selectedVariant.price);
    if (this.priceDisplay) {
      this.priceDisplay.innerHTML = priceHtml;
    }
    if (this.addButton) {
      this.addButton.disabled = !this.selectedVariant.available;
    }
  }

  getEffectivePrice(priceInCents) {
    if (!this.discountEnabled) return priceInCents;
    const multiplier = Math.max(0, 100 - this.discountPercent) / 100;
    const discounted = Math.round(priceInCents * multiplier);
    return Math.max(0, discounted);
  }

  formatCurrency(cents) {
    const amount = cents / 100;
    const formatted = Number.isInteger(amount) ? amount.toFixed(0) : amount.toFixed(2);
    return `${this.currency}${formatted}`;
  }

  getPriceHtml(finalPrice, originalPrice, { wrapOriginal = false } = {}) {
    if (finalPrice <= 0) {
      const freeLabel = this.discountNote || 'FREE';
      const label = wrapOriginal ? `(${freeLabel})` : freeLabel;
      const compare = originalPrice > 0 ? ` <s>${this.formatCurrency(originalPrice)}</s>` : '';
      return `${label}${compare}`;
    }

    if (originalPrice > finalPrice) {
      return `${this.formatCurrency(finalPrice)} <s>${this.formatCurrency(originalPrice)}</s>`;
    }

    const original = this.formatCurrency(originalPrice);
    return wrapOriginal ? `(${original})` : original;
  }

  addToProduct() {
    if (!this.selectedVariant) {
      this.setErrorMessage('Please select a valid option.');
      return;
    }

    if (!this.selectedVariant.available) {
      this.setErrorMessage('Selected option is unavailable.');
      return;
    }

    this.setErrorMessage('');
    const finalPrice = this.getEffectivePrice(this.selectedVariant.price);
    if (this.cartContext) {
      this.addStrapToCart(finalPrice);
    } else {
      this.updateGiftWidget(finalPrice);
      this.close();
    }
  }

  setErrorMessage(message) {
    if (this.errorEl) {
      this.errorEl.textContent = message;
    }
  }

  updateGiftWidget(finalPriceCents) {
    const widget = document.querySelector('[data-wrist-strap-widget]');
    if (!widget) return;

    const baseTitle = widget.dataset.baseTitle || '';
    const colorLabel = this.getDisplayColor() || this.selectedVariant.options[0];
    const titleEl = widget.querySelector('[data-wrist-strap-title]');
    const baseEl = widget.querySelector('[data-wrist-strap-base]');
    const colorEl = widget.querySelector('[data-wrist-strap-color]');
    const colorWrapper = widget.querySelector('[data-wrist-strap-color-wrapper]');
    const imageEl = widget.querySelector('.gift-widget__img img');
    const priceEl = widget.querySelector('[data-wrist-strap-price]');
    const chargeInput = widget.querySelector('[data-wrist-strap-charge-input]');
    const idInput = widget.querySelector('[data-wrist-strap-id-input]');
    const triggerBtn = this.activeTrigger || widget.querySelector('[data-wrist-strap-trigger]');

    const baseText = baseTitle || this.selectedVariant.title;
    if (baseEl) {
      baseEl.textContent = baseText;
    } else if (titleEl) {
      titleEl.textContent = `${baseText} - ${colorLabel}`;
    }
    if (colorEl) {
      colorEl.textContent = colorLabel;
    }
    if (colorWrapper) {
      colorWrapper.removeAttribute('hidden');
    }

    if (priceEl) {
      priceEl.innerHTML = this.getPriceHtml(finalPriceCents, this.selectedVariant.price, { wrapOriginal: true });
    }

    if (chargeInput) {
      chargeInput.value = (finalPriceCents / 100).toString();
    }

    if (idInput) {
      idInput.value = this.selectedVariant.id;
    }

    widget.dataset.selectedVariantId = this.selectedVariant.id;

    if (triggerBtn) {
      triggerBtn.setAttribute('data-price', (finalPriceCents / 100).toFixed(2));
    }

    const giftWidget = document.querySelector('gift-widget');
    if (giftWidget && typeof giftWidget.addGiftProduct === 'function' && triggerBtn) {
      giftWidget.addGiftProduct(triggerBtn);
    } else if (giftWidget && triggerBtn) {
      giftWidget.handleBtn(triggerBtn);
      giftWidget.handleFields(triggerBtn);
      giftWidget.handlePrice();
    }
  }

  addStrapToCart(finalPriceCents) {
    const cartItems = document.querySelector('cart-drawer-items') || document.querySelector('cart-items');
    if (!cartItems || !this.cartContext) return;

    const line = Number(this.cartContext.line) || 1;
    const quantity = Number(this.cartContext.quantity) || 1;
    const charges = (finalPriceCents / 100).toString();
    const surrogateTarget = {
      dataset: {
        isDiscount: this.cartContext.isDiscount || 'false',
        discount: this.cartContext.discount || '0',
      },
    };

    cartItems.updateComponent(
      cartItems,
      line,
      quantity,
      'wrist',
      this.selectedVariant.id,
      charges,
      surrogateTarget,
    );

    this.close();
    this.openCartDrawer();
  }

  updateCtaLabel() {
    if (!this.ctaText) return;
    this.ctaText.textContent = this.cartContext ? 'Add to bag' : 'Add to product';
  }

  closeCartDrawer() {
    if (this.cartContext && this.cartDrawer) {
      this.cartDrawer.close(true);
    }
  }

  openCartDrawer() {
    if (this.cartDrawer) {
      this.cartDrawer.open();
    }
  }
}

customElements.define('wrist-strap-drawer', WristStrapDrawer);
